import React, { useState } from 'react';
import { X, Shield, Settings, BarChart3, Check } from 'lucide-react';
import { Button } from './ui/button';
import { setConsent, getConsent } from '../utils/cookieConsent';

const CookieSettings = ({ onClose }) => {
  const existingConsent = getConsent();
  const [preferences, setPreferences] = useState({
    functional: existingConsent?.preferences?.functional || false,
    analytics: existingConsent?.preferences?.analytics || false
  });

  const handleToggle = (category) => {
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    setConsent(preferences);
    onClose();
  };

  const cookieCategories = [
    {
      id: 'essential',
      name: 'Cookies Essenciais',
      description: 'Estes cookies são necessários para o funcionamento básico do site e não podem ser desativados. Incluem cookies de sessão e preferências de utilizador.',
      icon: Shield,
      color: 'green',
      required: true,
      examples: 'Cookies de sessão, preferências de idioma, consentimento de cookies'
    },
    {
      id: 'functional',
      name: 'Cookies Funcionais',
      description: 'Estes cookies permitem funcionalidades melhoradas e personalização, como vídeos e chat ao vivo.',
      icon: Settings,
      color: 'blue',
      required: false,
      examples: 'Preferências de utilizador, configurações de interface'
    },
    {
      id: 'analytics',
      name: 'Cookies de Analytics',
      description: 'Estes cookies ajudam-nos a entender como os visitantes interagem com o site, fornecendo informações sobre métricas como número de visitantes, taxa de rejeição e origem do tráfego.',
      icon: BarChart3,
      color: 'purple',
      required: false,
      examples: 'Vercel Analytics, métricas de utilização, análise de tráfego web'
    }
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Settings Panel */}
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Definições de Cookies</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fechar"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <p className="text-gray-600 leading-relaxed">
            Utilizamos diferentes tipos de cookies no nosso site. Pode escolher quais categorias deseja permitir. 
            Note que desativar alguns tipos de cookies pode afetar a sua experiência no site.
          </p>

          {/* Cookie Categories */}
          <div className="space-y-4">
            {cookieCategories.map((category) => {
              const Icon = category.icon;
              const isEnabled = category.required || preferences[category.id];
              const bgColor = {
                green: 'bg-green-50 border-green-200',
                blue: 'bg-blue-50 border-blue-200',
                purple: 'bg-purple-50 border-purple-200'
              }[category.color];
              const iconColor = {
                green: 'text-green-600',
                blue: 'text-blue-600',
                purple: 'text-purple-600'
              }[category.color];

              return (
                <div key={category.id} className={`border rounded-xl p-5 ${bgColor}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-12 h-12 ${bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className={iconColor} size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-gray-900">{category.name}</h3>
                          {category.required && (
                            <span className="text-xs bg-gray-900 text-white px-2 py-1 rounded-full font-semibold">
                              Obrigatório
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                        <p className="text-xs text-gray-500">
                          <strong>Exemplos:</strong> {category.examples}
                        </p>
                      </div>
                    </div>
                    
                    {/* Toggle */}
                    <button
                      onClick={() => !category.required && handleToggle(category.id)}
                      disabled={category.required}
                      className={`relative w-14 h-8 rounded-full transition-colors flex-shrink-0 ${
                        isEnabled ? 'bg-orange-600' : 'bg-gray-300'
                      } ${category.required ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      aria-label={`${isEnabled ? 'Desativar' : 'Ativar'} ${category.name}`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform flex items-center justify-center ${
                          isEnabled ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      >
                        {isEnabled && <Check size={14} className="text-orange-600" />}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Mais Informações</h4>
            <p className="text-sm text-gray-600 mb-2">
              Para saber mais sobre como utilizamos cookies e como protegemos os seus dados, consulte:
            </p>
            <div className="flex flex-wrap gap-2">
              <a href="/politica-cookies" className="text-sm text-orange-600 hover:text-orange-700 underline">
                Política de Cookies
              </a>
              <span className="text-gray-400">•</span>
              <a href="/politica-privacidade" className="text-sm text-orange-600 hover:text-orange-700 underline">
                Política de Privacidade
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleSave}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold"
              size="lg"
            >
              Guardar Preferências
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-gray-300 text-gray-700 hover:bg-white"
              size="lg"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettings;
